import { DateTime } from "luxon";
import * as yup from "yup";

import { UserFeatureFlag } from "../../constants";
import {
  ResourceOwnerSetting,
  resourceOwnerSettingSchema,
} from "../../types/resourceOwner";
import { UserSetting } from "../../types/user";
import { ApiClientConstructor, _BaseApiClient } from "../base";

export interface ResourceOwnerApiClient {
  updateResourceOwnerSetting: (
    setting: ResourceOwnerSetting,
    shouldIgnoreConflict: boolean,
    resourceOwnerId?: string
  ) => Promise<UserSetting>;
  subscribePlan: (planId: string, resourceOwnerId?: string) => Promise<string>;
  manageSubscription: (
    flow?: "payment_method_update" | "subscription_cancel",
    resourceOwnerId?: string
  ) => Promise<string>;
  isPaymentRequired: (resourceOwnerId?: string) => Promise<boolean>;
  getSetting: (resourceOwnerId?: string) => Promise<ResourceOwnerSetting>;
  setPlan: (
    plan: "free" | "trial" | "enterprise",
    quota?: number,
    endAt?: Date,
    resourceOwnerId?: string,
    region?: string
  ) => Promise<void>;
  setQuota: (
    softLimit: number | null,
    hardLimit: number | null,
    usageCap: number | null,
    resourceOwnerId?: string,
    region?: string
  ) => Promise<void>;
  setTeamEnabledFeatures: (
    features: UserFeatureFlag[],
    enabledAuditLog: boolean,
    resourceOwnerId?: string,
    region?: string
  ) => Promise<void>;
  getReadmeIOAuthToken: (
    resourceOwnerId: string,
    region: string,
    apiEndpoint: string,
    workerEndpoint: string,
    formId?: string,
    workspaceId?: string
  ) => Promise<string>;
}

export function withResourceOwnerApi<
  TBase extends ApiClientConstructor<_BaseApiClient>
>(Base: TBase) {
  return class extends Base {
    async updateResourceOwnerSetting(
      setting: UserSetting,
      shouldIgnoreConflict: boolean,
      resourceOwnerId?: string
    ): Promise<UserSetting> {
      const args = this.injectOptionalFields(
        {
          setting: this.injectOptionalFields(
            {},
            {
              google_service_account_key: setting.googleServiceAccountKey,
              azure_endpoint: setting.azureEndpoint,
              azure_subscription_key: setting.azureSubscriptionKey,
            }
          ),
        },
        {
          resource_owner_id: resourceOwnerId,
          retrieved_at: shouldIgnoreConflict ? undefined : setting.updatedAt,
        }
      );

      return await this.lambda(
        "resource_owner:update-setting",
        args,
        resourceOwnerSettingSchema
      );
    }

    subscribePlan(planId: string, resourceOwnerId?: string): Promise<string> {
      return this.lambda(
        "resource_owner:subscribe-plan",
        this.injectOptionalFields(
          {
            plan_id: planId,
          },
          {
            resource_owner_id: resourceOwnerId,
          }
        ),
        yup.string().required()
      );
    }

    manageSubscription(
      flow?: string,
      resourceOwnerId?: string
    ): Promise<string> {
      return this.lambda(
        "resource_owner:manage-subscription",
        this.injectOptionalFields(
          {
            flow: flow,
          },
          {
            resource_owner_id: resourceOwnerId,
          }
        ),
        yup.string().required()
      );
    }

    isPaymentRequired(resourceOwnerId?: string): Promise<boolean> {
      return this.lambda(
        "resource_owner:is-payment-required",
        this.injectOptionalFields({}, { resource_owner_id: resourceOwnerId }),
        yup.boolean().required()
      );
    }

    getSetting(resourceOwnerId?: string): Promise<ResourceOwnerSetting> {
      return this.lambda(
        "resource_owner:get-setting",
        this.injectOptionalFields(
          {},
          {
            resource_owner_id: resourceOwnerId,
          }
        ),
        resourceOwnerSettingSchema.required()
      );
    }

    setPlan(
      plan: "trial" | "enterprise",
      quota?: number,
      endAt?: Date,
      resourceOwnerId?: string,
      region?: string
    ): Promise<void> {
      return this.lambda(
        "resource_owner:set-plan",
        this.injectOptionalFields(
          { plan },
          {
            resource_owner_id: resourceOwnerId,
            quota,
            end_at: endAt ? DateTime.fromJSDate(endAt).toISODate() : undefined,
          }
        ),
        undefined,
        null,
        region ? { region } : undefined
      );
    }

    setQuota(
      softLimit: number | null,
      hardLimit: number | null,
      usageCap: number | null,
      resourceOwnerId?: string,
      region?: string
    ): Promise<void> {
      return this.lambda(
        "resource_owner:set-quota",
        {
          resource_owner_id: resourceOwnerId,
          soft_limit: softLimit,
          hard_limit: hardLimit,
          usage_cap: usageCap,
        },
        undefined,
        null,
        region ? { region } : undefined
      );
    }

    setTeamEnabledFeatures(
      features: UserFeatureFlag[],
      enabledAuditLog: boolean,
      resourceOwnerId?: string,
      region?: string
    ): Promise<void> {
      return this.lambda(
        "resource_owner:set-enabled-features",
        this.injectOptionalFields(
          {},
          {
            resource_owner_id: resourceOwnerId,
            enabled_features: features,
            enabled_audit_log: enabledAuditLog,
          }
        ),
        undefined,
        null,
        region ? { region } : undefined
      );
    }

    getReadmeIOAuthToken(
      resourceOwnerId: string,
      region: string,
      apiEndpoint: string,
      workerEndpoint: string,
      formId?: string,
      workspaceId?: string
    ): Promise<string> {
      return this.lambda(
        "resource_owner:get-readme-io-auth-token",
        this.injectOptionalFields(
          {
            resource_owner_id: resourceOwnerId,
            region: region,
            api_endpoint: apiEndpoint,
            worker_endpoint: workerEndpoint,
          },
          {
            form_id: formId,
            workspace_id: workspaceId,
          }
        ),
        yup.string().required()
      );
    }
  };
}
